import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H3 } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant, ButtonSize } from '@amzn/stencil-react-components/button';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { IconBinSmall } from '@amzn/stencil-react-icons';

/** Header title toggles the expander on click. Module-level so the emotion css
 *  object is reference-stable. */
const CLICKABLE_TITLE = { cursor: 'pointer' } as const;

export interface SubQuestionRowHeaderProps {
  /** The Expander's composed title text (e.g. "1. My sub-question"). Stencil's
   *  Expander types this as possibly-undefined, so accept undefined explicitly
   *  (exactOptionalPropertyTypes). */
  readonly titleText?: string | undefined;
  /** Toggles the surrounding Expander. */
  readonly onToggle: () => void;
  /** Response-type label shown in the badge (e.g. "Pass / Fail"). */
  readonly typeBadgeLabel: string;
  /** When true, the badge is Negative/red (this row blocks conditional
   *  display — a Pass/Fail sub); otherwise Primary/blue. */
  readonly highlightAsInvalid: boolean;
  readonly onDelete: (e: React.MouseEvent) => void;
  readonly deleteAltText: string;
  readonly disabled: boolean;
  readonly dataTestId?: string;
}

/**
 * The collapsed row header for a sub-question: the composed title (click to
 * expand), a response-type {@link Badge} so the type is scannable without
 * expanding (Warning-colored when this row blocks conditional display), and a
 * right-anchored delete button. Extracted from {@link SubQuestionRow} to keep
 * that file under the size limit.
 */
export function SubQuestionRowHeader({
  titleText,
  onToggle,
  typeBadgeLabel,
  highlightAsInvalid,
  onDelete,
  deleteAltText,
  disabled,
  dataTestId,
}: SubQuestionRowHeaderProps): JSX.Element {
  return (
    <Row alignItems="center" gridGap="dimensions.spacing.200" flex={1} minWidth={0}>
      <H3 onClick={onToggle} css={CLICKABLE_TITLE}>
        {titleText}
      </H3>
      <Badge
        type={highlightAsInvalid ? BadgeType.Negative : BadgeType.Primary}
        value={typeBadgeLabel}
        {...(dataTestId ? { dataTestId: `${dataTestId}-type-badge` } : {})}
      />
      <Col flex={1} minWidth={0} />
      <Button
        variant={ButtonVariant.Tertiary}
        size={ButtonSize.Small}
        icon={<IconBinSmall aria-hidden="true" />}
        aria-label={deleteAltText}
        onClick={onDelete}
        disabled={disabled}
        data-testid={dataTestId ? `${dataTestId}-delete` : undefined}
      />
    </Row>
  );
}
